const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

class Drawer {
  static openDrawers = new Set();

  constructor({
    drawerID,
    buttonID,
    extraInteractiveIDs = [],
    onToggle,
    onDomLoaded,
    drawerAnimations = { enter: { x: ['0%', '100%'] }, exit: { x: ['100%', '0%'] } },
  }) {
    this.id = drawerID;
    this.drawerAnimations = drawerAnimations;
    this.buttonID = buttonID;
    this.extraInteractiveIDs = extraInteractiveIDs;
    this.isDrawerOpen = false;
    this.animationInProgress = false;
    this.onToggle = onToggle;
    this.previouslyFocused = null;
    this.handleKeydown = this.handleKeydown.bind(this);
    const initDOM = () => {
      this.button = this.buttonID ? document.getElementById(this.buttonID) : null;
      this.drawer = document.getElementById(this.id);
      if (!this.drawer) return;
      this.drawer.toggleDrawer = this.toggle.bind(this);
      this.drawer.setAttribute('data-drawer-open', `${this.isDrawerOpen}`);
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.drawer.setAttribute('aria-hidden', `${!this.isDrawerOpen}`);
      this.drawer.inert = !this.isDrawerOpen;
      if (!this.drawer.hasAttribute('tabindex')) {
        this.drawer.setAttribute('tabindex', '-1');
      }
      if (this.button) {
        this.button.setAttribute('data-drawer-open', `${this.isDrawerOpen}`);
        if (!this.button.hasAttribute('aria-expanded')) {
          this.button.setAttribute('aria-expanded', 'false');
        }
        if (!this.button.hasAttribute('aria-controls')) {
          this.button.setAttribute('aria-controls', this.id);
        }
        this.button.addEventListener('click', () => {
          this.toggle();
        });
      }
      onDomLoaded?.(this.drawer, this.button);
      this.drawer.addEventListener('toggleDrawer', this.onToggle);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initDOM);
    } else {
      initDOM();
    }
  }

  getFocusableElements() {
    if (!this.drawer) return [];
    return Array.from(this.drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
      return !el.hasAttribute('aria-hidden') && el.offsetParent !== null;
    });
  }

  handleKeydown(e) {
    if (!this.isDrawerOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.toggle();
      return;
    }

    if (e.key === 'Tab') {
      const focusables = this.getFocusableElements();
      const cycle = this.button ? [this.button, ...focusables] : focusables;
      if (cycle.length === 0) {
        e.preventDefault();
        this.drawer.focus();
        return;
      }
      const first = cycle[0];
      const last = cycle[cycle.length - 1];
      const active = document.activeElement;
      const drawerFirst = focusables[0];

      if (e.shiftKey) {
        if (active === first || active === this.drawer) {
          e.preventDefault();
          last.focus();
        } else if (this.button && drawerFirst && active === drawerFirst) {
          e.preventDefault();
          this.button.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      } else if (this.button && active === this.button && drawerFirst) {
        e.preventDefault();
        drawerFirst.focus();
      }
    }
  }

  focusFirstElement() {
    const focusables = this.getFocusableElements();
    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      this.drawer.focus();
    }
  }

  setOutsideInert(makeInert) {
    if (!this.drawer || !document.body) return;
    const allowed = [this.drawer];
    if (this.button) allowed.push(this.button);
    this.extraInteractiveIDs.forEach((id) => {
      const el = document.getElementById(id);
      if (el) allowed.push(el);
    });
    this.walkInert(document.body, allowed, makeInert);
  }

  walkInert(parent, allowed, makeInert) {
    Array.from(parent.children).forEach((node) => {
      if (allowed.includes(node)) return;
      if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
      if (allowed.some((a) => node.contains(a))) {
        this.walkInert(node, allowed, makeInert);
        return;
      }
      if (makeInert) {
        if (!node.hasAttribute('data-drawer-prev-inert')) {
          node.setAttribute('data-drawer-prev-inert', node.inert ? 'true' : 'false');
        }
        node.inert = true;
      } else if (node.hasAttribute('data-drawer-prev-inert')) {
        node.inert = node.dataset.drawerPrevInert === 'true';
        node.removeAttribute('data-drawer-prev-inert');
      }
    });
  }

  async toggle() {
    const drawerAnimation = !this.isDrawerOpen ? this.drawerAnimations.enter : this.drawerAnimations.exit;
    const { animate } = window.Motion;

    if (this.animationInProgress) return;
    this.animationInProgress = true;

    // Exclusive policy: before opening this drawer, close any other open drawer.
    // The while loop yields until the other drawer finishes any in-flight animation,
    // otherwise its toggle() would early-return on animationInProgress and leak it open.
    if (!this.isDrawerOpen) {
      for (const other of Array.from(Drawer.openDrawers)) {
        if (other === this) continue;
        while (other.animationInProgress) {
          await new Promise((r) => requestAnimationFrame(r));
        }
        if (other.isDrawerOpen) {
          await other.toggle();
        }
      }
    }

    const wasClosed = !this.isDrawerOpen;
    this.isDrawerOpen = !this.isDrawerOpen;
    this.drawer.setAttribute('data-drawer-open', `${this.isDrawerOpen}`);
    this.drawer.setAttribute('aria-hidden', `${!this.isDrawerOpen}`);
    if (this.button) {
      this.button.setAttribute('data-drawer-open', `${this.isDrawerOpen}`);
      this.button.setAttribute('aria-expanded', `${this.isDrawerOpen}`);
    }

    if (wasClosed) {
      // Register before the animation so concurrent toggles on other drawers
      // can see this one as "open" and wait/close it via the exclusive policy.
      Drawer.openDrawers.add(this);
      this.previouslyFocused = document.activeElement;
      this.drawer.inert = false;
      document.addEventListener('keydown', this.handleKeydown);
      this.setOutsideInert(true);
    }

    await animate(this.drawer, drawerAnimation, { duration: 0.4, ease: !this.isDrawerOpen ? 'easeOut' : 'easeIn' });
    this.animationInProgress = false;

    if (this.isDrawerOpen) {
      this.focusFirstElement();
    } else {
      this.drawer.inert = true;
      this.setOutsideInert(false);
      document.removeEventListener('keydown', this.handleKeydown);
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
      this.previouslyFocused = null;
      Drawer.openDrawers.delete(this);
    }

    this.toggleEvent = new CustomEvent('toggleDrawer', {
      detail: {
        drawerID: this.id,
        buttonID: this.buttonID,
        button: document.getElementById(this.buttonID),
        drawer: document.getElementById(this.id),
        isOpen: this.isDrawerOpen,
      },
    });
    this.drawer.dispatchEvent(this.toggleEvent);
  }
}
